/**
 * @jeraisonne/db
 * Types et constantes liés au schéma Supabase de Jeraisonne.
 */

export * from './types';
export * from './challenge-types';
export * from './challenge-stream';
export * from './blog';
export * from './newsletter';
export * from './clue-hunt';
export * from './clue-hunt-source';
export * from './alwaysdata';
export * from './tutor-kind';
export * from './lesson-visuals';

// ============================================================================
// Constantes UI / labels
// ============================================================================
import type { FormationType, RendezVousKind, SchoolLevel, SubjectSlug, UserRole } from './types';

export const SCHOOL_LEVELS: ReadonlyArray<{
  value: SchoolLevel;
  label: string;
  cycle: 'collège' | 'lycée' | 'prépa';
}> = [
  { value: '6e', label: '6ème', cycle: 'collège' },
  { value: '5e', label: '5ème', cycle: 'collège' },
  { value: '4e', label: '4ème', cycle: 'collège' },
  { value: '3e', label: '3ème', cycle: 'collège' },
  { value: '2nde', label: '2nde', cycle: 'lycée' },
  { value: '1re', label: '1ère', cycle: 'lycée' },
  { value: 'tle', label: 'Terminale', cycle: 'lycée' },
  { value: 'mpsi', label: 'MPSI', cycle: 'prépa' },
  { value: 'ptsi', label: 'PTSI', cycle: 'prépa' },
  { value: 'mp', label: 'MP', cycle: 'prépa' },
  { value: 'pt', label: 'PT', cycle: 'prépa' },
] as const;

/** Niveaux du secondaire (6e → Tle) : Live School, enfants gérés, parcours « Élève ». */
export const SECONDARY_SCHOOL_LEVELS = SCHOOL_LEVELS.filter((l) => l.cycle !== 'prépa');

/** Classes préparatoires scientifiques (migration 0302) : 1re année MPSI/PTSI, 2e année MP/PT. */
export const CPGE_SCHOOL_LEVELS = SCHOOL_LEVELS.filter((l) => l.cycle === 'prépa');

export function isCpgeLevel(level: string | null | undefined): boolean {
  return CPGE_SCHOOL_LEVELS.some((l) => l.value === level);
}

/**
 * Formations post-bac (profil "Étudiant", student_profiles.is_higher_ed_student).
 * Orthogonal à SCHOOL_LEVELS — jamais utilisé pour filtrer du contenu K-12.
 */
export const FORMATION_TYPES: ReadonlyArray<{ value: FormationType; label: string }> = [
  { value: 'licence', label: 'Licence' },
  { value: 'bts', label: 'BTS' },
  { value: 'but', label: 'BUT' },
  { value: 'cpge', label: 'Classe préparatoire (CPGE)' },
  { value: 'ingenieur', label: "École d'ingénieur" },
  { value: 'ecole_commerce', label: 'École de commerce' },
  { value: 'master', label: 'Master' },
  { value: 'doctorat', label: 'Doctorat' },
  { value: 'autre', label: 'Autre formation' },
] as const;

export const RENDEZ_VOUS_KIND_LABELS: Record<RendezVousKind, string> = {
  cours: 'Cours',
  express: 'Appel rapide',
  etude: "Session d'étude",
  entretien: 'Entretien académique',
};

export const SUBJECTS: ReadonlyArray<{
  value: SubjectSlug;
  label: string;
  emoji: string;
  /** Niveaux scolaires pour lesquels cette matière existe dans le curriculum. */
  levels: ReadonlyArray<SchoolLevel>;
}> = [
  { value: 'math', label: 'Mathématiques', emoji: '📐', levels: ['6e', '5e', '4e', '3e', '2nde'] },
  { value: 'physique', label: 'Physique-Chimie', emoji: '⚛️', levels: ['5e', '4e', '3e', '2nde'] },
  { value: 'sciences', label: 'Sciences', emoji: '🔬', levels: ['6e'] },
  { value: 'math-spe', label: 'Maths Spécialité', emoji: '∑', levels: ['1re', 'tle'] },
  { value: 'physique-spe', label: 'Physique-Chimie Spé', emoji: '🧪', levels: ['1re', 'tle'] },
  { value: 'math-expertes', label: 'Maths Expertes', emoji: '🔢', levels: ['tle'] },
  { value: 'math-complementaires', label: 'Maths Complémentaires', emoji: '🧮', levels: ['tle'] },
  {
    value: 'enseignement-scientifique',
    label: 'Maths Enseignement Scientifique',
    emoji: '🔭',
    levels: ['1re'],
  },
  { value: 'math-cpge', label: 'Maths prépa', emoji: '🎓', levels: ['mpsi', 'ptsi', 'mp', 'pt'] },
] as const;

/**
 * Matières EN PRÉPARATION (chantier multi-matières, migrations 0364-0366).
 * Elles existent dans l'enum `subject_slug` et ont leurs programmes + packs en
 * BROUILLON, mais ne sont PAS dans `SUBJECTS` : invisibles pour les élèves,
 * parents, profs et le site. Seul l'admin des packs (`/tutor/admin/packs`) les
 * propose, pour relire et préparer le contenu. Pour en ouvrir une, la déplacer
 * dans `SUBJECTS` (et étendre `SubjectSlug`).
 */
export const UPCOMING_SUBJECTS: ReadonlyArray<{
  value: string;
  label: string;
  emoji: string;
  levels: ReadonlyArray<SchoolLevel>;
}> = [
  {
    value: 'francais',
    label: 'Français',
    emoji: '📖',
    levels: ['6e', '5e', '4e', '3e', '2nde', '1re'],
  },
  { value: 'philosophie', label: 'Philosophie', emoji: '🦉', levels: ['tle'] },
  {
    value: 'histoire-geo',
    label: 'Histoire-géographie',
    emoji: '🗺️',
    levels: ['6e', '5e', '4e', '3e', '2nde', '1re', 'tle'],
  },
  {
    value: 'emc',
    label: 'Enseignement moral et civique',
    emoji: '⚖️',
    levels: ['6e', '5e', '4e', '3e', '2nde', '1re', 'tle'],
  },
  { value: 'hggsp', label: 'HGGSP', emoji: '🌍', levels: ['1re', 'tle'] },
  { value: 'svt', label: 'SVT', emoji: '🧬', levels: ['5e', '4e', '3e', '2nde'] },
  { value: 'svt-spe', label: 'SVT Spé', emoji: '🔬', levels: ['1re', 'tle'] },
  { value: 'ses', label: 'SES', emoji: '📊', levels: ['2nde'] },
  { value: 'ses-spe', label: 'SES Spé', emoji: '📈', levels: ['1re', 'tle'] },
  { value: 'technologie', label: 'Technologie', emoji: '⚙️', levels: ['5e', '4e', '3e'] },
] as const;

export const USER_ROLES: ReadonlyArray<{ value: UserRole; label: string; description: string }> = [
  { value: 'student', label: 'Élève', description: 'Je suis au collège ou au lycée' },
  { value: 'parent', label: 'Parent', description: 'Je veux suivre la progression de mon enfant' },
  { value: 'tutor', label: 'Professeur', description: 'Je donne des cours particuliers' },
  {
    value: 'player',
    label: 'Curieux',
    description: 'Je veux faire des exercices pour le plaisir',
  },
] as const;

// Âge légal pour le consentement digital en France (RGPD art. 8)
export const PARENTAL_CONSENT_AGE_THRESHOLD = 15;
